export type Result<T, E = unknown> = { isSuccess: true; value: T } | { isSuccess: false; error: E };

/**
 * Runs a closure and wraps the return value in a Result<T>. The `isSuccess`
 * value is `false` if the close throws, otherwise it's `true`.
 */
export async function Result<T>(block: () => T | Promise<T>): Promise<Result<T>> {
  try {
    const value: T = await block();
    return { isSuccess: true as const, value };
  } catch (error) {
    return { isSuccess: false as const, error };
  }
}

/**
 * The difference between this and `Promise.all` is that `Promise.all` rejects
 * when any promise rejects, and this resolves when all promises resolve _or_
 * reject.
 *
 * This function returns an array of results, each of which is either a success
 * result containing the resolved promise, or an error result containing the
 * value that was passed to `reject()`.
 *
 * This function itself never throws or rejects.
 */
export const allToResolveOrReject = async <T>(promises: Promise<T>[]): Promise<Result<T>[]> => {
  // Attach a dummy rejection handler to each promise as soon as possible, to
  // try to prevent them from rejecting in the background without a handler, as
  // that can cause issues, depending on the runtime environment.
  //
  promises.forEach(handleRejection); // try to comment this out & run the tests

  const results: Result<T>[] = [];

  for (const promise of promises) {
    try {
      const value = await promise;
      results.push({ isSuccess: true, value });
    } catch (error) {
      results.push({ isSuccess: false, error });
    }
  }

  return results;
};

/**
 * Attach a dummy rejection handler to a promise and return the original
 * promise. Used to prevent unhandled rejections in the background.
 *
 * To use this, wrap any `async` or `Promise`-returning function with this
 * function and use it as you otherwise would.
 *
 * Calling this multiple times for the same promise should have no ill effect.
 *
 * @example
 * // Before
 * await fetch('https://hevy.com/blabla');
 *
 * // After
 * await handleRejection(fetch('https://hevy.com/blabla'));
 */
const handleRejection = <T>(p: Promise<T>): Promise<T> => {
  p.catch(() => {});
  return p;
};

type Stage3MethodDecorator<T> = (
  value: (...args: any[]) => T,
  context: ClassMemberDecoratorContext,
) => (...args: any[]) => T;

export function synchronized(queue: boolean): Stage3MethodDecorator<Promise<any>>;

export function synchronized(queue: boolean, id: any): Stage3MethodDecorator<Promise<any>>;

export function synchronized(
  value: (...args: any[]) => any,
  context: ClassMemberDecoratorContext,
): void;

/**
 * Do not use this directly. Use the alias provided by the parent project.
 */
export function synchronized(
  arg0: unknown,
  arg1?: unknown,
): Stage3MethodDecorator<Promise<any>> | ((this: any, ...args: any[]) => Promise<any>) {
  if (typeof arg1 === 'object' && arg1 !== null && 'name' in arg1 && 'access' in arg1) {
    return synchronizedStage3DecoratorFactory(
      arg0 as any,
      arg1! as ClassMemberDecoratorContext,
      true,
    );
  } else {
    return function (value: any, context: ClassMemberDecoratorContext) {
      return synchronizedStage3DecoratorFactory(value, context, arg0 as boolean, arg1);
    };
  }
}

const $queue = Symbol();

abstract class LockError extends Error {
  abstract readonly propertyKey: string | symbol;
  abstract readonly lockId: unknown;

  protected constructor(...args: any) {
    super(...args);
  }
}

class $LockError extends LockError {
  readonly propertyKey: string | symbol;
  readonly lockId: unknown;

  constructor({
    message: baseMessage,
    propertyKey,
    lockId,
  }: {
    message: string;
    propertyKey: string | symbol;
    lockId: unknown;
  }) {
    const formattedLockId = lockId !== undefined ? `"${String(lockId)}"` : '(anonymous)';

    const message = `Cannot invoke "${String(
      propertyKey,
    )}" (locked by ${formattedLockId}): ${baseMessage}`;

    super(message);

    this.name = 'LockError';
    this.message = message;
    this.propertyKey = propertyKey;
    this.lockId = lockId;
  }
}

function synchronizedStage3DecoratorFactory(
  origFn: (...args: any[]) => any,
  context: ClassMemberDecoratorContext,
  wait: boolean,
  id?: any,
): (this: any, ...args: any[]) => Promise<any> {
  const lockId = arguments.length === 4 ? id : Symbol();
  const propertyKey = context.name;

  return synchronizedDecoratorFactory({
    id,
    lockId,
    origFn,
    propertyKey,
    wait,
  });
}

function synchronizedDecoratorFactory({
  id,
  lockId,
  origFn,
  propertyKey,
  wait,
}: {
  id: any;
  lockId: any;
  origFn: (...args: any[]) => Promise<any> | any;
  propertyKey: string | symbol;
  wait: boolean;
}) {
  return async function (this: any, ...args: any[]) {
    const queue: Promise<unknown>[] = ((this[$queue] ??= {})[lockId] ??= []);

    if (queue.length > 0 && !wait) {
      throw new $LockError({
        message: 'Operation is already in progress',
        propertyKey,
        lockId: id,
      });
    }

    let done!: () => void;
    const thisCall = new Promise<void>(resolve => {
      done = resolve;
    });

    const waiting = [...queue];
    queue.push(thisCall);
    await allToResolveOrReject(waiting);

    try {
      return await origFn.apply(this, args);
    } finally {
      done();

      if (queue.shift() !== thisCall) {
        // eslint-disable-next-line no-unsafe-finally
        throw new $LockError({
          message: 'Assertion failed: @synchronized queue is mangled',
          propertyKey,
          lockId: id,
        });
      }
    }
  };
}
